// SidebarNavigation.js — the menu button and the menu drawer.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  IconButton,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import HelpIcon from "@mui/icons-material/Help";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArticleIcon from "@mui/icons-material/Article";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import ShieldIcon from "@mui/icons-material/Shield";
import TableChartIcon from "@mui/icons-material/TableChart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExtensionIcon from "@mui/icons-material/Extension";
import HistoryIcon from "@mui/icons-material/History";
import QuizIcon from "@mui/icons-material/Quiz";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import { BRAND, accentTint } from "./theme";

export function MenuButton({ onClick }) {
  return (
    <IconButton
      onClick={onClick}
      aria-label="Open menu"
      sx={{
        width: 40,
        height: 40,
        color: "#e6e8ee",
        transition: "color 160ms ease",
        "&:hover": { color: BRAND.accent, background: accentTint(0.08) },
      }}
    >
      <MenuIcon sx={{ fontSize: 24 }} />
    </IconButton>
  );
}

// The menu (every screen width; the only navigation below 1200px), grouped
// under small headings, the games near the top. Items are real links, so
// they open in a new tab and read as links to screen readers.
const GROUPS = [
  { items: [{ label: "Home", icon: <HomeIcon />, page: "home", path: "/" }] },
  {
    title: "Play",
    items: [
      { label: "Scorigami Daily", icon: <ExtensionIcon />, page: "daily", path: "/daily" },
      { label: "Mystery Season", icon: <QuizIcon />, page: "season", path: "/daily/season" },
      { label: "Higher or Lower", icon: <SwapVertIcon />, page: "hl", path: "/higher-or-lower" },
      { label: "Past puzzles", icon: <HistoryIcon />, page: "archive", path: "/daily/archive" },
    ],
  },
  {
    title: "Scorelines",
    items: [
      { label: "Every scoreline", icon: <ScoreboardIcon />, page: "scorelines", path: "/scorelines" },
      { label: "Records", icon: <EmojiEventsIcon />, page: "records", path: "/records" },
      { label: "Half-time & full-time", icon: <TimelapseIcon />, page: "halftime", path: "/half-time-full-time" },
      { label: "Scorigami Watch", icon: <VisibilityIcon />, page: "watch", path: "/watch" },
    ],
  },
  {
    title: "Clubs & seasons",
    items: [
      { label: "Clubs", icon: <ShieldIcon />, page: "clubs", path: "/clubs" },
      { label: "Head to head", icon: <CompareArrowsIcon />, page: "h2h", path: "/head-to-head" },
      { label: "All-time table", icon: <TableChartIcon />, page: "table", path: "/all-time-table" },
      { label: "Seasons", icon: <CalendarMonthIcon />, page: "seasons", path: "/seasons" },
      { label: "Race chart", icon: <ShowChartIcon />, page: "race", path: "/race" },
    ],
  },
  {
    title: "More",
    items: [
      { label: "Articles", icon: <ArticleIcon />, page: "articles", path: "/articles" },
      { label: "About", icon: <InfoIcon />, page: "about", path: "/about" },
      { label: "FAQ", icon: <HelpIcon />, page: "faq", path: "/faq" },
      { label: "Contact", icon: <MailOutlineIcon />, page: "contact", path: "/contact" },
    ],
  },
];

export default function SidebarNavigation({ currentPage, drawerOpen, onDrawerToggle }) {
  return (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => onDrawerToggle(false)}
      PaperProps={{
        sx: {
          background: "rgba(25, 25, 30, 0.95)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(255,255,255,0.1)",
          width: 280,
        },
      }}
    >
      <Box component="nav" aria-label="Menu" sx={{ py: 1.5 }}>
        {GROUPS.map((group, g) => (
          <List
            key={group.title || g}
            dense
            disablePadding
            sx={{ mb: 0.5 }}
            subheader={
              group.title && (
                <ListSubheader
                  disableSticky
                  sx={{ background: "transparent", color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", lineHeight: "32px", mt: 1, pl: 2.5 }}
                >
                  {group.title}
                </ListSubheader>
              )
            }
          >
            {group.items.map((item) => {
              const on = currentPage === item.page;
              return (
                <ListItem key={item.page} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={on}
                    aria-current={on ? "page" : undefined}
                    onClick={() => onDrawerToggle(false)}
                    sx={{
                      py: 1.1,
                      pl: 2.5,
                      borderLeft: "4px solid transparent",
                      "&.Mui-selected": {
                        background: BRAND.accentSoft,
                        borderLeft: `4px solid ${BRAND.accent}`,
                      },
                      "&:hover": { background: "rgba(255,255,255,0.05)" },
                    }}
                  >
                    <ListItemIcon sx={{ color: on ? BRAND.accent : BRAND.textSecondary, minWidth: 42 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{ color: on ? BRAND.accent : "#fff", "& .MuiTypography-root": { fontWeight: on ? 700 : 500, fontSize: 15 } }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        ))}
      </Box>
    </Drawer>
  );
}
